import {
  Box,
  ButtonBase,
  Icon,
  Paper,
  Popper,
  Grow,
  styled,
} from "@mui/material";
import { useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Span } from "../Typography";

// STYLED COMPONENTS
const HorizontalNavRoot = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  height: 64,
  gap: "1.5rem",
}));

const NavItemButton = styled(ButtonBase)(({ theme }) => ({
  height: 44,
  color: theme.palette.text.primary,
  padding: "0 12px",
  textTransform: "capitalize",
  fontFamily: "var(--font-family-sans)",
  borderRadius: 10,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

const NavIcon = styled(Icon)(() => ({
  fontSize: "18px",
  marginRight: "8px",
}));

const ExternalLinkButton = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: 44,
  padding: "0 12px",
  color: theme.palette.text.primary,
  textDecoration: "none",
  borderRadius: 10,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

export default function MatxHorizontalNav({ items = [] }) {
  const { t } = useTranslation(["common"]);
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuItems, setSubmenuItems] = useState([]);
  const [open, setOpen] = useState(false);

  const popperRef = useRef();

  const handleHoverMenu = (event, children) => {
    setAnchorEl(event.currentTarget);
    setSubmenuItems(children);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // ✅ parent active if any child path matches
  const isAnyChildActive = (children = []) => {
    return children.some((c) => location.pathname.startsWith(c.path));
  };

  const renderMenuItem = (item, index) => {
    if (item.type === "label") return null;

    // ✅ With submenu
    if (item.children?.length) {
      const parentActive = isAnyChildActive(item.children);

      return (
        <Box
          key={index}
          onMouseEnter={(e) => handleHoverMenu(e, item.children)}
          onMouseLeave={handleClose}
          ref={popperRef}
        >
          <NavItemButton
            sx={{
              fontWeight: parentActive ? 900 : 600,
              backgroundColor: parentActive
                ? "rgba(255,255,255,0.10)"
                : "transparent",
            }}
          >
            {item.icon && <NavIcon>{item.icon}</NavIcon>}
            <Span>{t(item.name)}</Span>
          </NavItemButton>
        </Box>
      );
    }

    // ✅ External link
    if (item.type === "extLink") {
      return (
        <ExternalLinkButton
          key={index}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.icon && <NavIcon>{item.icon}</NavIcon>}
          <Span>{t(item.name)}</Span>
        </ExternalLinkButton>
      );
    }

    // ✅ Normal link
    return (
      <NavLink
        key={index}
        to={item.path}
        end={item.path === "/patients"} // ✅ FIX: exact match only for /patients
        style={{ textDecoration: "none" }}
      >
        {({ isActive }) => (
          <NavItemButton
            sx={{
              fontWeight: isActive ? 900 : 600,
              backgroundColor: isActive
                ? "rgba(255,255,255,0.12)"
                : "transparent",
            }}
          >
            {item.icon && <NavIcon>{item.icon}</NavIcon>}
            <Span>{t(item.name)}</Span>
          </NavItemButton>
        )}
      </NavLink>
    );
  };

  return (
    <HorizontalNavRoot onMouseLeave={handleClose}>
      {items.map(renderMenuItem)}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={150}>
            <Paper
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={handleClose}
              elevation={3}
              sx={{
                mt: 1,
                minWidth: 220,
                background: "#1b1b1b",
                color: "#fff",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {submenuItems.map((child, idx) => (
                <NavLink
                  key={idx}
                  to={child.path}
                  end
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    textDecoration: "none",
                    padding: "10px 16px",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.85)",
                    fontWeight: isActive ? 900 : 600,
                    background: isActive
                      ? "rgba(255,255,255,0.12)"
                      : "transparent",
                  })}
                  onClick={handleClose}
                >
                  {child.icon && <NavIcon>{child.icon}</NavIcon>}
                  {t(child.name)}
                </NavLink>
              ))}
            </Paper>
          </Grow>
        )}
      </Popper>
    </HorizontalNavRoot>
  );
}
